/**
 * Client-side extraction of an epub's Dublin Core metadata (`<dc:title>` and
 * `<dc:creator>`), so the upload form can pre-fill a real book title and author
 * instead of the raw (often numeric) filename.
 *
 * An epub is a ZIP archive. Rather than pull a ~95KB ZIP library into the main
 * bundle, we parse the archive's central directory by hand and inflate only the
 * one entry we need (the OPF package document) using the browser-native
 * `DecompressionStream`. The OPF is located via `META-INF/container.xml`,
 * falling back to the first `*.opf` entry.
 *
 * Every failure path returns empty/undefined fields so callers can fall back to
 * the filename — missing metadata must never block an upload.
 */

export interface EpubMetadata {
  title?: string;
  author?: string;
}

const DUBLIN_CORE_NS = "http://purl.org/dc/elements/1.1/";

const EOCD_SIGNATURE = 0x06054b50; // End Of Central Directory
const CENTRAL_FILE_SIGNATURE = 0x02014b50; // Central directory file header
const LOCAL_FILE_SIGNATURE = 0x04034b50; // Local file header

const STORED = 0;
const DEFLATED = 8;

interface ZipEntry {
  compressionMethod: number;
  compressedSize: number;
  localHeaderOffset: number;
}

/**
 * Read an epub blob and return its Dublin Core title and author. Any field that
 * can't be found is left `undefined` (not a valid zip, no OPF, no such tag,
 * unsupported compression, …), and an unreadable epub yields `{}`.
 */
export const getEpubMetadata = async (file: Blob): Promise<EpubMetadata> => {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const entries = readCentralDirectory(bytes);
    if (!entries) return {};

    const opfPath = await findOpfPath(bytes, entries);
    if (!opfPath) return {};

    const opfXml = await readEntryAsText(bytes, entries.get(opfPath));
    if (!opfXml) return {};

    const doc = new DOMParser().parseFromString(opfXml, "application/xml");
    return {
      title: readDublinCore(doc, "title"),
      author: readDublinCore(doc, "creator"),
    };
  } catch {
    return {};
  }
};

/**
 * Read the trimmed text of the first `<dc:{tag}>` element, handling both the
 * namespaced form and the `dc:`-prefixed form (happy-dom doesn't resolve the
 * namespace, so the prefixed lookup is the working fallback).
 */
function readDublinCore(doc: Document, tag: string): string | undefined {
  const element =
    doc.getElementsByTagNameNS(DUBLIN_CORE_NS, tag)[0] ??
    doc.getElementsByTagName(`dc:${tag}`)[0];
  return element?.textContent?.trim() || undefined;
}

/**
 * Parse the ZIP central directory into a map of entry name → metadata needed to
 * locate and decompress that entry. Returns `undefined` if the archive doesn't
 * look like a ZIP.
 */
function readCentralDirectory(
  bytes: Uint8Array
): Map<string, ZipEntry> | undefined {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder("utf-8");

  // The EOCD record lives near the end, after an optional comment. Scan
  // backwards for its signature (the comment is almost always empty).
  let eocdOffset = -1;
  for (let offset = bytes.length - 22; offset >= 0; offset--) {
    if (view.getUint32(offset, true) === EOCD_SIGNATURE) {
      eocdOffset = offset;
      break;
    }
  }
  if (eocdOffset < 0) return undefined;

  const entryCount = view.getUint16(eocdOffset + 10, true);
  let offset = view.getUint32(eocdOffset + 16, true); // start of central directory

  const entries = new Map<string, ZipEntry>();
  for (let index = 0; index < entryCount; index++) {
    if (view.getUint32(offset, true) !== CENTRAL_FILE_SIGNATURE) break;

    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);

    const name = decoder.decode(
      bytes.subarray(offset + 46, offset + 46 + nameLength)
    );
    entries.set(name, {
      compressionMethod,
      compressedSize,
      localHeaderOffset,
    });

    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

/**
 * Read and decompress a single ZIP entry, decoding it as UTF-8 text.
 */
async function readEntryAsText(
  bytes: Uint8Array,
  entry: ZipEntry | undefined
): Promise<string | undefined> {
  if (!entry) return undefined;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerOffset = entry.localHeaderOffset;
  if (view.getUint32(headerOffset, true) !== LOCAL_FILE_SIGNATURE) {
    return undefined;
  }

  // The local header's name/extra lengths can differ from the central
  // directory's, so the data offset must be computed from the local header.
  const nameLength = view.getUint16(headerOffset + 26, true);
  const extraLength = view.getUint16(headerOffset + 28, true);
  const dataStart = headerOffset + 30 + nameLength + extraLength;
  const compressed = bytes.subarray(
    dataStart,
    dataStart + entry.compressedSize
  );

  let raw: Uint8Array;
  if (entry.compressionMethod === STORED) {
    raw = compressed;
  } else if (entry.compressionMethod === DEFLATED) {
    raw = await inflateRaw(compressed);
  } else {
    return undefined;
  }

  return new TextDecoder("utf-8").decode(raw);
}

/**
 * Inflate raw DEFLATE-compressed bytes using the browser/Node-native
 * `DecompressionStream`, letting the platform manage the stream lifecycle.
 */
async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const decompressed = new Blob([data as BufferSource])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(decompressed).arrayBuffer());
}

/**
 * Locate the OPF package document: read `META-INF/container.xml` for the
 * declared rootfile, falling back to the first entry whose name ends in `.opf`.
 */
async function findOpfPath(
  bytes: Uint8Array,
  entries: Map<string, ZipEntry>
): Promise<string | undefined> {
  const containerXml = await readEntryAsText(
    bytes,
    entries.get("META-INF/container.xml")
  );
  if (containerXml) {
    const doc = new DOMParser().parseFromString(
      containerXml,
      "application/xml"
    );
    const fullPath = doc
      .getElementsByTagName("rootfile")[0]
      ?.getAttribute("full-path");
    if (fullPath && entries.has(fullPath)) return fullPath;
  }

  for (const name of entries.keys()) {
    if (/\.opf$/i.test(name)) return name;
  }
  return undefined;
}
